import React, { useMemo, useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import { Gantt, Willow, WillowDark } from "@svar-ui/react-gantt";
import { SelectButton } from 'primereact/selectbutton';
import { Dropdown } from 'primereact/dropdown';
import { ProgressBar } from 'primereact/progressbar';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Panel } from 'primereact/panel';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import axios from "axios";
import "@svar-ui/react-gantt/all.css";
import { Tag } from 'primereact/tag';
import { Checkbox } from 'primereact/checkbox';

import ProductionMetricsMeasuresSummary from "../productionmetricsmeasuressummary/ProductionMetricsMeasuresSummary";
import ProductionitemListComponent from "../productionitem/ProductionitemListComponent";
import KanbanComponent from "../kanbanview/KanbanComponent";

const ProductionOverview = (props) => {
  const { t } = props;
  const VIEW_TYPE_STORAGE_KEY = 'productionOverviewViewType'; 
  const GANTT_HORIZON_STORAGE_KEY = 'productionOverviewGanttHorizonDays';
  const GANTT_SCALE_STORAGE_KEY = 'productionOverviewGanttScaleMode';
  const [dark, setDark] = useState(false);
  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [viewType, setViewType] = useState(() => {
    return localStorage.getItem(VIEW_TYPE_STORAGE_KEY) || 'gantt';
  });
  const [viewMode, setViewMode] = useState("planned");
  const [dialogVisible, setDialogVisible] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [productionStatus, setProductionStatus] = useState([]);
  const [draggedJobId, setDraggedJobId] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [dragOverStatusKey, setDragOverStatusKey] = useState(null);
  const [selectedJobProjectItemId, setSelectedJobProjectItemId] = useState(null);

  const DS_STORAGE_KEY = 'lastProductionOverviewDetailedSearchForm';

  const savedDetailedForm = localStorage.getItem(DS_STORAGE_KEY)
    ? JSON.parse(localStorage.getItem(DS_STORAGE_KEY))
    : null;

  const [ganttHorizonDays, setGanttHorizonDays] = useState(() => {
    const saved = parseInt(localStorage.getItem(GANTT_HORIZON_STORAGE_KEY), 10);
    return Number.isFinite(saved) && saved > 0 ? saved : 14;
  });

  const [ganttScaleMode, setGanttScaleMode] = useState(() => {
    return localStorage.getItem(GANTT_SCALE_STORAGE_KEY) || 'hour';
  });

  const [detailedSearchVisible, setDetailedSearchVisible] = useState(false);
  const [detailedSearchQuery, setDetailedSearchQuery] = useState(
    savedDetailedForm?.detailedSearchQuery || ''
  );

  const [dsPartner, setDsPartner] = useState(savedDetailedForm?.dsPartner || '');
  const [dsProjectNumber, setDsProjectNumber] = useState(savedDetailedForm?.dsProjectNumber || '');
  const [dsTechnologies, setDsTechnologies] = useState(savedDetailedForm?.dsTechnologies || []);
  const [dsEmployees, setDsEmployees] = useState(savedDetailedForm?.dsEmployees || []);
  const [dsMyTasks, setDsMyTasks] = useState(savedDetailedForm?.dsMyTasks || false);
  const [currentEmployeeId, setCurrentEmployeeId] = useState(null);

  const [technologies, setTechnologies] = useState([]);
  const [employees, setEmployees] = useState([]);

  const viewTypeOptions = [
    { label: t('gantt'), value: "gantt" },
    { label: t('kanban'), value: "kanban" },
    { label: t('list'), value: "list" }
  ];

  const viewOptions = [
    { label: t('planned'), value: "planned" },
    { label: t('actual'), value: "actual" }
  ];

  const ganttScaleOptions = [
    { label: t('hourly'), value: "hour" },
    { label: t('daily'), value: "day" }
  ];

  const ganttHorizonOptions = [
    { label: t('days2'), value: 2 },
    { label: t('days7'), value: 7 },
    { label: t('days14'), value: 14 },
    { label: t('days30'), value: 30 },
    { label: t('days60'), value: 60 }
  ];

  const apiCall = (method, url, data) => {
    return axios({
      method: method,
      url: process.env.REACT_APP_API_HOST + url,
      data,
      headers: {
        'Authorization': 'Bearer ' + localStorage.getItem("userToken"),
        'App-Network': localStorage.getItem("networkId")
      }
    });
  };

  const persistDetailedForm = (query) => {
    localStorage.setItem(DS_STORAGE_KEY, JSON.stringify({
      detailedSearchQuery: query,
      dsPartner,
      dsProjectNumber,
      dsTechnologies,
      dsEmployees,
      dsMyTasks
    }));
  };

  const clearDetailedFormPersist = () => {
    localStorage.removeItem(DS_STORAGE_KEY);
  };

  const buildDetailedSearchQuery = () => {
    const parts = [];
    const like = (field, value) => {
      const v = (value || '').toString().trim();
      if (!v) {
        return;
      }
      parts.push(`search[${field}][operator]=like&search[${field}][value]=*${encodeURIComponent(v)}*`);
    };
    like('partner_name', dsPartner);
    like('project_number', dsProjectNumber);
    if (Array.isArray(dsTechnologies) && dsTechnologies.length > 0) {
      dsTechnologies.forEach((technologyId) => {
        parts.push(`search[technology][operator]=in&search[technology][value][]=${encodeURIComponent(technologyId)}`);
      });
    }
    if (dsMyTasks && currentEmployeeId) {
      parts.push(`search[employee][operator]=%3D&search[employee][value]=${encodeURIComponent(currentEmployeeId)}`);
    } else if (Array.isArray(dsEmployees) && dsEmployees.length > 0) {
      dsEmployees.forEach((employeeId) => {
        parts.push(`search[employee][operator]=in&search[employee][value][]=${encodeURIComponent(employeeId)}`);
      });
    }
    return parts.filter(Boolean).join('&');
  };

  const doDetailedSearch = () => {
    const q = buildDetailedSearchQuery();
    setDetailedSearchQuery(q);
    persistDetailedForm(q);
  };

  const clearDetailedSearch = () => {
    setDsPartner('');
    setDsProjectNumber('');
    setDsTechnologies([]);
    setDsEmployees([]);
    setDsMyTasks(false);
    setDetailedSearchQuery('');
    clearDetailedFormPersist();
  };

  const handleViewTypeChange = (value) => {
    if (!value) return;
    setViewType(value);
    localStorage.setItem(VIEW_TYPE_STORAGE_KEY, value);
  };

  const handleGanttScaleModeChange = (value) => {
    if (!value) return;
    setGanttScaleMode(value);
    localStorage.setItem(GANTT_SCALE_STORAGE_KEY, value);
  };

  const handleGanttHorizonChange = (value) => {
    const parsedValue = parseInt(value, 10);
    if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
      return;
    }
    setGanttHorizonDays(parsedValue);
    localStorage.setItem(GANTT_HORIZON_STORAGE_KEY, parsedValue.toString());
  };

  const getProjectItemIdFromJob = (job) => {
    return (
      job?.project_item?.id ||
      job?.project_item ||
      job?.project_item_id ||
      job?.production_item?.project_item?.id ||
      job?.production_item?.project_item ||
      null
    );
  };

  const getProductionItemIdFromJob = (job) => {
    return (
      job?.production_item?.id ||
      job?.production_item_id ||
      null
    );
  };

  const getProjectItemIdFromProductionItem = (productionItem) => {
    return (
      productionItem?.project_item?.id ||
      productionItem?.project_item ||
      productionItem?.project_item_id ||
      productionItem?.projectItem?.id ||
      productionItem?.projectItemId ||
      null
    );
  };

  const loadProductionJob = (id) => {
    if (!id) return;

    setDialogVisible(true);
    setDialogLoading(true);
    setSelectedJob(null);
    setSelectedJobProjectItemId(null);

    apiCall('get', `/api/v2/production-job/${id}`)
      .then((response) => {
        const job = response?.data?.data || null;

        setSelectedJob(job);

        const directProjectItemId = getProjectItemIdFromJob(job);

        if (directProjectItemId) {
          setSelectedJobProjectItemId(directProjectItemId);
          return;
        }

        const productionItemId = getProductionItemIdFromJob(job);

        if (!productionItemId) {
          setSelectedJobProjectItemId(null);
          return;
        }

        apiCall('get', `/api/v2/production-item/${productionItemId}`)
          .then((productionItemResponse) => {
            const productionItem = productionItemResponse?.data?.data || null;
            const projectItemId = getProjectItemIdFromProductionItem(productionItem);

            setSelectedJobProjectItemId(projectItemId);
          })
          .catch((error) => {
            window.App.toastShow('error', t('error'), error.toString(), '', error.response);
            setSelectedJobProjectItemId(null);
          });
      })
      .catch((error) => {
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
        setSelectedJob(null);
        setSelectedJobProjectItemId(null);
      })
      .finally(() => {
        setDialogLoading(false);
      });
  };

  const updateJobStatus = (jobId, statusKey) => {
    if (!jobId || !statusKey || statusUpdating) return;
    const job = jobs.find((item) => item.id === jobId);
    if (!job) return;
    const currentStatus = getStatusKey(job);
    if (currentStatus === statusKey) {
      setDraggedJobId(null);
      setDragOverStatusKey(null);
      return;
    }
    setStatusUpdating(true);
    apiCall('patch', `/api/v2/production-job/${jobId}`, {
      status: statusKey
    })
      .then(() => {
        setJobs((prevJobs) => prevJobs.map((item) => {
          if (item.id !== jobId) {
            return item;
          }
          return {
            ...item,
            status: statusKey
          };
        }));
        window.App.toastShow('success', 'Státusz módosítva!');
      })
      .catch((error) => {
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      })
      .finally(() => {
        setStatusUpdating(false);
        setDraggedJobId(null);
        setDragOverStatusKey(null);
      });
  };

  useEffect(() => {
    apiCall('get', '/api/v2/dictionary/production-status')
      .then((response) => {
        setProductionStatus(response?.data?.data || []);
      })
      .catch((error) => {
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      });
  }, []);

  const getSelectedJobProjectItemId = () => {
    return (
      selectedJob?.project_item?.id ||
      selectedJob?.project_item_id ||
      selectedJob?.production_item?.project_item?.id ||
      selectedJob?.production_item?.project_item_id ||
      selectedJob?.production_item?.projectItem?.id ||
      null
    );
  };

  const loadProductionJobs = () => {
    setLoading(true);
    let query = '';
    if (detailedSearchQuery) {
      query = '?' + detailedSearchQuery;
    }
    apiCall("get", "/api/v2/production-job" + query)
      .then((response) => {
        setJobs(response.data.data.items || []);
      })
      .catch((error) => {
        window.App.toastShow("error", t("error"), error.toString(), '', error.response);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    loadProductionJobs();
  }, [detailedSearchQuery]);

  useEffect(() => {
    apiCall('get', '/api/v2/technology?item_per_page=999')
      .then((response) => {
        setTechnologies(response?.data?.data?.items || []);
      })
      .catch((error) => {
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      });
    apiCall('get', '/api/v2/employee?item_per_page=999')
      .then((response) => {
        setEmployees(response?.data?.data?.items || []);
      })
      .catch((error) => {
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      });
    apiCall('get', '/api/v2/profile')
      .then((response) => {
        const employeeId = response?.data?.data?.employee?.id || null;
        setCurrentEmployeeId(employeeId);
      })
      .catch((error) => {
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      });
  }, []);

  const ActionCell = ({ row }) => {
    const roles = localStorage.getItem('roles') || '';

    return (
      <>
        {roles.toString().includes('role_') && (
          <Button
            icon="pi pi-eye"
            tooltip={t('view')}
            tooltipOptions={{ position: 'left' }}
            onClick={() => loadProductionJob(row?.id)}
          />
        )}
      </>
    );
  };

  const getStatusKey = (job) => {
    const status =
      job?.status?.key ||
      job?.status?.id ||
      job?.status?.code ||
      job?.status ||
      job?.production_status?.key ||
      job?.production_status?.id ||
      job?.production_status?.code ||
      job?.production_status;

    return status ? status.toString().toUpperCase() : 'UNKNOWN';
  };

  const getProductionStatusSeverity = (status) => {
    const key = status ? status.toString().toUpperCase() : '';
    switch (key) {
      case 'NEW':
        return 'primary';
      case 'SCHEDULED':
        return 'secondary';
      case 'IN_PROGRESS':
        return 'info';
      case 'PAUSED':
      case 'SUSPENDED':
        return 'warning';
      case 'DONE':
        return 'success';
      case 'DELETED':
        return 'danger';
      default:
        return null;
    }
  };

  const getStatusLabelByKey = (key) => {
    const status = productionStatus.find((item) => item.key === key);

    return status?.value || key || 'Nincs státusz';
  };

  const getProductionStatusLabel = (status) => {
    if (!status) return '-';
    const key = status.toString().toUpperCase();
    const item = productionStatus.find((statusItem) => statusItem.key === key);
    return item?.value || key;
  };

  const renderProductionStatusBadge = (status) => {
    const label = getProductionStatusLabel(status);
    return (
      <Tag
        value={label}
        severity={getProductionStatusSeverity(status)}
        rounded
      />
    );
  };

  const kanbanColumns = useMemo(() => {
    const grouped = {};

    jobs.forEach((job) => {
      const key = getStatusKey(job);

      if (!grouped[key]) {
        grouped[key] = [];
      }

      grouped[key].push(job);
    });

    const orderedColumns = productionStatus.map((status) => ({
      key: status.key,
      label: status.value,
      items: grouped[status.key] || []
    }));

    const unknownColumns = Object.keys(grouped)
      .filter((key) => !productionStatus.some((status) => status.key === key))
      .map((key) => ({
        key,
        label: getStatusLabelByKey(key),
        items: grouped[key]
      }));

    return [
      ...orderedColumns,
      ...unknownColumns
    ];
  }, [jobs, productionStatus]);

  const getDateValue = (value) => {
    if (!value) return '-';
    return window.App.formatDateValue(value);
  };

  const getJobTitle = (job) => {
    return job?.production_item?.name ||
      job?.technology?.name ||
      'Ismeretlen feladat';
  };

  const getJobSubtitle = (job) => {
    const parts = [];

    if (job?.project_number) {
      parts.push(job.project_number);
    }

    if (job?.technology?.name) {
      parts.push(job.technology.name);
    }

    return parts.length > 0 ? parts.join(' — ') : '-';
  };

  const { tasks, links, scales, columns, ganttStart, ganttEnd, lengthUnit, cellWidth } = useMemo(() => {
    const transformedTasks = jobs
      .filter((job) => {
        if (viewMode === "planned") {
          return job.scheduled_from && job.scheduled_until;
        }
        return job.produced_from && job.produced_until;
      })
      .map((job) => {
        const startStr = viewMode === "planned" ? job.scheduled_from : job.produced_from;
        const endStr = viewMode === "planned" ? job.scheduled_until : job.produced_until;

        const parseDate = (str) => str ? new Date(str.replace(' ', 'T')) : null;

        return {
          id: job.id,
          text: `${job.technology?.name || "Ismeretlen technológia"} — ${job.project_number || "N/A"}`,
          start: parseDate(startStr),
          end: parseDate(endStr),
          type: "task",
          status: getProductionStatusLabel(job.status),
          status_key: job.status,
          progress: job.status === 'DONE' ? 100 : 0,
          css: viewMode === "actual" ? "gantt-fact-task" : "gantt-plan-task"
        };
      });

    const columns = [
      { id: "text", header: "Task name", width: 260 },
      { id: "status", header: "Státusz", width: 150, cell: ({ row }) => renderProductionStatusBadge(row?.status_key) },
      { id: "start", header: "Start date", width: 140 },
      { id: "duration", header: "Duration", width: 100, align: "center" },
      { id: "actions", header: "", width: 90, cell: ActionCell }
    ];

    const taskDates = transformedTasks
      .map((task) => task.start)
      .filter(Boolean)
      .map((date) => date.getTime());

    const baseStart = taskDates.length > 0
      ? new Date(Math.min(...taskDates))
      : new Date();

    baseStart.setHours(0, 0, 0, 0);

    const baseEnd = new Date(baseStart);
    baseEnd.setDate(baseEnd.getDate() + ganttHorizonDays - 1);
    baseEnd.setHours(23, 59, 59, 999);

    const scales = ganttScaleMode === 'day'
      ? [
          { unit: "month", step: 1, format: "%Y. %M" },
          { unit: "day", step: 1, format: "%j." },
        ]
      : [
          { unit: "day", step: 1, format: "%Y. %M %j." },
          { unit: "hour", step: 1, format: "%H:%i" },
        ];

    const lengthUnit = ganttScaleMode === 'day' ? 'day' : 'hour';
    const cellWidth = ganttScaleMode === 'day' ? 80 : 60;

    return {
      tasks: transformedTasks,
      links: [],
      scales,
      columns,
      ganttStart: baseStart,
      ganttEnd: baseEnd,
      lengthUnit,
      cellWidth
    };
  }, [jobs, viewMode, t, productionStatus, ganttHorizonDays, ganttScaleMode]);

  const Theme = dark ? WillowDark : Willow;

  return (
    <div className="ProductionOverview widget">
      {loading && (
        <ProgressBar
          mode="indeterminate"
          style={{
            height: "5px",
            position: "absolute",
            top: 0,
            left: 0,
            borderRadius: 0,
            zIndex: 10
          }}
        />
      )}

      <div
        className="p-d-flex p-ai-center p-jc-between p-mb-3 p-p-2"
        style={{
          gap: 12,
          background: 'var(--surface-b)',
          borderRadius: '6px'
        }}
      >
        <div style={{ fontWeight: 600, fontSize: "1.1rem" }}>
          <i className="pi pi-chart-bar p-mr-2"></i>
          {t('App\\Production\\Productionoverview')}
        </div>

        <div className="p-d-flex p-ai-center" style={{ gap: 10, flexWrap: 'wrap' }}>
          <Button
            type="button"
            label={t('detailedSearch')}
            icon={detailedSearchQuery ? "pi pi-filter-fill" : "pi pi-filter"}
            className="p-button-outlined"
            onClick={() => setDetailedSearchVisible(!detailedSearchVisible)}
          />

          <SelectButton
            value={viewType}
            options={viewTypeOptions}
            onChange={(e) => handleViewTypeChange(e.value)}
            disabled={loading}
          />

          {viewType === 'gantt' && (
            <>
              <SelectButton
                value={viewMode}
                options={viewOptions}
                onChange={(e) => e.value && setViewMode(e.value)}
                disabled={loading}
              />

              <Dropdown
                value={ganttScaleMode}
                options={ganttScaleOptions}
                onChange={(e) => handleGanttScaleModeChange(e.value)}
                disabled={loading}
                placeholder={t('dsResolution')}
                style={{ minWidth: 120 }}
              />

              <Dropdown
                value={ganttHorizonDays}
                options={ganttHorizonOptions}
                onChange={(e) => handleGanttHorizonChange(e.value)}
                disabled={loading}
                placeholder={t('dsTimeHorizon')}
                style={{ minWidth: 130 }}
              />
            </>
          )}

          {/* <Button
            icon={dark ? "pi pi-sun" : "pi pi-moon"}
            className="p-button-rounded p-button-text p-button-secondary"
            onClick={() => setDark(!dark)}
            tooltip={dark ? t('lightMode') : t('darkMode')}
          /> */}
        </div>
      </div>

      

      

      {detailedSearchVisible && (
        <Panel
          className="p-mb-3"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
              doDetailedSearch();
            }
          }}
        >
          <div className="p-grid">
            <div className="p-col-12 p-lg-4">
              <div className="p-field p-fluid">
                <label>{t('columnPartner')}</label>
                <InputText
                  value={dsPartner}
                  onChange={(e) => setDsPartner(e.target.value)}
                />
              </div>
            </div>

            <div className="p-col-12 p-lg-4">
              <div className="p-field p-fluid">
                <label>{t('columnProjectNumber')}</label>
                <InputText
                  value={dsProjectNumber}
                  onChange={(e) => setDsProjectNumber(e.target.value)}
                />
              </div>
            </div>

            <div className="p-col-12 p-lg-4">
              <div className="p-field p-fluid">
                <label>{t('columnTechnology2')}</label>
                <MultiSelect
                  value={dsTechnologies}
                  options={technologies.map((technology) => ({
                    label: technology.name,
                    value: technology.id
                  }))}
                  onChange={(e) => setDsTechnologies(e.value)}
                  placeholder={t('dsSelectTechnology')}
                  display="chip"
                  filter
                  showClear
                  appendTo={document.body}
                />
              </div>
            </div>

            <div className="p-col-12 p-lg-4">
              <div className="p-field p-fluid">
                <label>{t('columnEmployee')}</label>
                <MultiSelect
                  value={dsEmployees}
                  options={employees.map((employee) => ({
                    label: employee.name,
                    value: employee.id
                  }))}
                  onChange={(e) => setDsEmployees(e.value)}
                  placeholder={t('dsSelectEmployee')}
                  display="chip"
                  filter
                  showClear
                  appendTo={document.body}
                />
              </div>
            </div>

            <div className="p-col-12 p-lg-4">
              <div className="p-field-checkbox p-mt-4">
                <Checkbox
                  inputId="dsMyTasks"
                  checked={dsMyTasks}
                  onChange={(e) => {
                    setDsMyTasks(e.checked);
                    if (e.checked) {
                      setDsEmployees([]);
                    }
                  }}
                  disabled={!currentEmployeeId}
                />
                <label htmlFor="dsMyTasks" className="p-ml-2">
                  {t('columnMyTasks')}
                </label>
              </div>
            </div>

            <div className="p-col-12 p-text-right">
              <Button
                type="button"
                label={t('dsClearCriteria')}
                icon="pi pi-times"
                className="p-button-danger p-mr-2"
                onClick={clearDetailedSearch}
              />

              <Button
                type="button"
                label={t('dsSearch')}
                icon="pi pi-search"
                className="p-button-primary"
                onClick={doDetailedSearch}
              />
            </div>
          </div>
        </Panel>
      )}

      {viewType === 'list' && (
        <div style={{ border: '1px solid var(--surface-d)', borderRadius: 6, overflow: 'hidden' }}>
          <ProductionitemListComponent
            productionJobs={jobs}
            onJobClick={(job) => loadProductionJob(job?.id)}
            detailedSearchQuery={detailedSearchQuery}
          />
        </div>
      )}

      {viewType === 'gantt' && (
        <div
          style={{
            height: "calc(100vh - 250px)",
            minHeight: 600,
            background: dark ? "#333" : "#fff",
            borderRadius: 6,
            overflow: "auto",
            border: "1px solid var(--surface-d)"
          }}
        >
          {tasks.length > 0 ? (
            <Theme>
              <Gantt
                tasks={tasks}
                links={links}
                scales={scales}
                columns={columns}
                start={ganttStart}
                end={ganttEnd}
                projectStart={ganttStart}
                projectEnd={ganttEnd}
                lengthUnit={lengthUnit}
                cellWidth={cellWidth}
              />
            </Theme>
          ) : (
            <div
              className="p-d-flex p-ai-center p-jc-center p-flex-column"
              style={{ height: "100%", opacity: 0.5 }}
            >
              <i className="pi pi-filter-slash p-mb-2" style={{ fontSize: '2rem' }}></i>
              <span>
                {loading ? t('loadingData') : t('noDisplayableProductionJobInView')}
              </span>
            </div>
          )}
        </div>
      )}

      {viewType === 'kanban' && (
        <div className="production-kanban-wrapper" >
          {kanbanColumns.length > 0 ? (
            <div className="production-kanban-board">
              {kanbanColumns.map((column) => (
                <div
                  className={
                    "production-kanban-column" +
                    (dragOverStatusKey === column.key ? " production-kanban-column-drag-over" : "")
                  }
                  key={column.key}
                >
                  <div className="production-kanban-column-header">
                    <span className="production-kanban-column-title">
                      {column.label}
                    </span>

                    <span className="production-kanban-count">
                      {column.items.length}
                    </span>
                  </div>

                  <div
                    className={
                      "production-kanban-items" +
                      (dragOverStatusKey === column.key ? " production-kanban-items-drag-over" : "")
                    }
                    onDragOver={(e) => {
                      e.preventDefault();

                      if (draggedJobId) {
                        setDragOverStatusKey(column.key);
                      }
                    }}
                    onDragLeave={(e) => {
                      const currentTarget = e.currentTarget;
                      const relatedTarget = e.relatedTarget;
                      if (!currentTarget.contains(relatedTarget)) {
                        setDragOverStatusKey(null);
                      }
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragOverStatusKey(null);
                      updateJobStatus(draggedJobId, column.key);
                    }}
                  >
                    {column.items.map((job) => (
                      <KanbanComponent
                        key={job.id}
                        job={job}
                        t={t}
                        draggable={!statusUpdating}
                        onView={(selectedJob) => loadProductionJob(selectedJob?.id)}
                        renderStatusBadge={renderProductionStatusBadge}
                        formatDateValue={window.App.formatDateValue}
                        onDragStart={() => {
                          setDraggedJobId(job.id);
                        }}
                        onDragEnd={() => {
                          setDraggedJobId(null);
                          setDragOverStatusKey(null);
                        }}
                        style={{
                          opacity: draggedJobId === job.id ? 0.5 : 1,
                          cursor: statusUpdating ? 'not-allowed' : 'move'
                        }}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div
              className="p-d-flex p-ai-center p-jc-center p-flex-column"
              style={{ height: "100%", opacity: 0.5 }}
            >
              <i className="pi pi-filter-slash p-mb-2" style={{ fontSize: '2rem' }}></i>
              <span>
                {loading ? t('loadingData') : t('noDisplayableProductionJob')}
              </span>
            </div>
          )}
        </div>
      )}

      <Dialog
        header={t('taskDetails')}
        visible={dialogVisible}
        style={{ width: '50vw' }}
        onHide={() => {
          setDialogVisible(false);
          setSelectedJob(null);
        }}
      >
        {dialogLoading && (
          <ProgressBar mode="indeterminate" style={{ height: '5px' }} />
        )}

        {!dialogLoading && selectedJob && (
          <>
            <div className="p-grid">
              <div className="p-col-12">
                <div
                  className="p-p-3"
                  style={{
                    border: '1px solid var(--surface-d)',
                    borderRadius: 6
                  }}
                >
                  <h4 className="p-mt-0 p-mb-3 text-primary">
                    Feladat
                  </h4>

                  <p>
                    <strong>Név:</strong>{' '}
                    {selectedJob?.production_item?.name || '-'}
                  </p>

                  <p>
                    <strong>Munkaszám:</strong>{' '}
                    {selectedJob?.project_number || '-'}
                  </p>

                  <p>
                    <strong>{t('status')}:</strong>{' '}
                    {renderProductionStatusBadge(selectedJob?.status)}
                  </p>

                  <p>
                    <strong>{t('quantity')}:</strong>{' '}
                    {selectedJob?.quantity || 0}{' '}
                    {selectedJob?.unit?.name || ''}
                  </p>

                  <p>
                    <strong>{t('externalReference')}:</strong>{' '}
                    {selectedJob?.production_item?.external_reference || '-'}
                  </p>
                </div>
              </div>
            </div>

            {/* <div className="p-mt-3">
              <h4 className="text-primary">
                Összesített termelési adatok
              </h4>
            </div> */}
          </>
        )}
        <ProductionMetricsMeasuresSummary
          productionJobId={selectedJob?.id}
          t={t}
          title={t('productionDataRecordedForTask')}
          emptyMessage={t('noProductionDataRecordedForTask')}
          showProjectItemDetails={false}
        />

        <ProductionMetricsMeasuresSummary
          projectItemId={selectedJobProjectItemId}
          t={t}
        />

        {/* <ProductionWorkflowMetricsForm
          projectItemId={selectedJobProjectItemId}
          productionJobId={selectedJob?.id}
          t={t}
          onSaved={() => setMetricsRefreshKey((prev) => prev + 1)}
        /> */}
      </Dialog>
    </div>
  );
};

export default withTranslation('common')(ProductionOverview);
